/**
 * CW-SF-R2 TKT-6 — Attainment This Quarter (CEO role).
 *
 * Closed-won this fiscal Q / quarterly target with progress bar.
 * Renders an empty-state CTA pointing to /admin/sf/quotas when no
 * quota row exists so the dark tile is self-explanatory.
 */
import { useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { useAutoRefresh } from '@/services/widgetRegistry';
import type { WidgetProps } from '@/types';
import { SfWidgetShell } from './SfWidgetShell';
import { buildSfWidgetUrl, formatCurrency, type SfWidgetEnvelope } from './sfWidgetUtils';

interface ApiResponse extends SfWidgetEnvelope {
  quota_missing: boolean;
  q_label: string | null;
  fiscal_year?: number;
  fiscal_quarter?: number;
  actual: number;
  won_count?: number;
  target: number | null;
  pct: number | null;
  source?: string | null;
  derived_from_annual?: boolean;
}

const REFRESH_S = 5 * 60;

function pctClass(pct: number | null): string {
  if (pct === null) return '';
  if (pct >= 100) return 'sf-attainment--green';
  if (pct >= 80) return 'sf-attainment--yellow';
  return 'sf-attainment--red';
}

export function SfAttainmentWidget({ config }: WidgetProps) {
  const fetcher = useCallback(
    () => apiFetch<ApiResponse>(buildSfWidgetUrl('/mod/cw/widgets/sf/attainment', config)),
    [config],
  );
  const { data, loading, error } = useAutoRefresh<ApiResponse>(fetcher, REFRESH_S);

  if (data?.quota_missing) {
    return (
      <div className="sf-widget sf-widget--attainment">
        <SfWidgetShell
          envelope={data}
          loading={loading}
          error={error}
          hasItems={true}
          emptyMessage=""
        >
          <div className="sf-attainment sf-attainment--empty">
            <div className="sf-attainment__empty-title">No quarterly quota set</div>
            <div className="sf-attainment__empty-body">
              Closed-won this Q: {formatCurrency(data.actual)} ({data.won_count ?? 0} won)
            </div>
            <RouterLink to="/admin/sf/quotas" className="sf-attainment__empty-cta">
              Set quota in admin →
            </RouterLink>
          </div>
        </SfWidgetShell>
      </div>
    );
  }

  const pct = data?.pct ?? null;
  const cls = pctClass(pct);

  return (
    <div className={`sf-widget sf-widget--attainment ${cls}`}>
      <SfWidgetShell
        envelope={data}
        loading={loading}
        error={error}
        hasItems={true}
        emptyMessage="—"
      >
        <div className="sf-attainment">
          <div className="sf-attainment__header">
            <span className="sf-attainment__label">{data?.q_label ?? '—'}</span>
            <span className="sf-attainment__pct">
              {pct !== null ? `${pct}%` : '—'}
            </span>
          </div>
          <svg className="sf-attainment__bar" viewBox="0 0 100 8" preserveAspectRatio="none" role="img" aria-label="Attainment progress">
            <rect x="0" y="0" width="100" height="8" rx="2" className="sf-attainment__bar-bg" />
            <rect x="0" y="0" width={Math.min(100, pct ?? 0)} height="8" rx="2" className="sf-attainment__bar-fill" />
          </svg>
          <div className="sf-attainment__numbers">
            <span>{formatCurrency(data?.actual ?? 0)}</span>
            <span className="sf-attainment__sep">/</span>
            <span>{formatCurrency(data?.target ?? 0)}</span>
          </div>
          {data?.derived_from_annual && (
            <div className="sf-attainment__note">Target derived from annual ÷ 4</div>
          )}
        </div>
      </SfWidgetShell>
    </div>
  );
}
